import { Flex, Stack, Text } from '@mantine/core';
import pluralize from 'pluralize';
import { useCallback, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import { useFileUploads } from 'src/data-capture/hooks/file-uploads/useFileUploads';
import { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import DrawerFlowScreenLayout from 'src/UI/drawer-flow/DrawerFlowScreenLayout';
import SelectableItemList from 'src/UI/drawer-flow/SelectableItemList';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { Client } from 'src/users';
import { useMeetingNote } from '../MeetingNoteProvider';

type MeetingNoteData = {
  client?: Client;
  selectedRecordingId?: string;
  selectedTemplateId?: string;
};

const AUDIO_MIME_TYPES = [
  'audio/mpeg',
  'audio/wav',
  'audio/x-wav',
  'audio/mp4',
  'video/mp4',
  'audio/aac',
  'audio/flac',
  'audio/ogg',
  'audio/webm',
];

const filterAudioFiles = (files: UploadedFile[]): UploadedFile[] => {
  return files.filter(file => AUDIO_MIME_TYPES.includes(file.file.fileType));
};

export default function SelectRecordingStep(props: StepProps<MeetingNoteData>) {
  const { data, setData } = props;
  const { files, isLoading } = useFileUploads({
    clientId: data.client?.id,
  });

  const items = useMemo(() => {
    const audioFiles = filterAudioFiles(files);

    return audioFiles.map(file => ({
      id: file.id,
      title: file.file.fileName,
      description: `Uploaded: ${new Date(file.file.createdAt).toLocaleDateString()}`,
    }));
  }, [files]);

  const setSelectedRecordingId = useCallback(
    (selectedRecordingId: string | undefined) => {
      setData({ ...data, selectedRecordingId });
    },
    [data, setData],
  );

  const handleSelectionChange = (selectedIds: string[]) => {
    setSelectedRecordingId(selectedIds[0] || undefined);
  };

  return (
    <DrawerFlowScreenLayout
      {...props}
      title="Select recording"
      description="This recording will be used to generate the meeting note"
      isLoading={isLoading}
      canGoForward={props.canGoForward && !!data.selectedRecordingId}
      footer={
        items.length > 0 && (
          <Stack gap="xxs">
            <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.10">
              Can&apos;t find the recording?
            </Text>
            <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.7">
              Upload the recording to <DocumentLink client={props.data.client} /> and it will appear
              here.
            </Text>
          </Stack>
        )
      }
    >
      <Flex direction="column" gap="xs" mih={0}>
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="black" data-testid="recording-count">
          {items.length} {pluralize('recording', items.length)} found
        </Text>
        <SelectableItemList
          items={items}
          hint="The information already uploaded to the client's profile, financial and cashflows areas will  be added as supplementary information in the meeting note."
          selectedItemIds={data.selectedRecordingId ? [data.selectedRecordingId] : []}
          setSelectedItemIds={handleSelectionChange}
          selectMultiple={false}
          emptyView={
            <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.10">
              Upload a meeting recording (MP3, MP4, or WAV) to{' '}
              <DocumentLink client={props.data.client} /> and it will appear here.
            </Text>
          }
        />
      </Flex>
    </DrawerFlowScreenLayout>
  );
}

function DocumentLink({ client }: { client: Client | undefined }) {
  const { close } = useMeetingNote();

  if (client == null) {
    return null;
  }

  return (
    <Text
      component={NavLink}
      to={`/client/${client.id}/document/add`}
      size="textXs"
      fw={FONT_WEIGHTS.medium}
      c="brand.8"
      onClick={close}
    >
      {client?.firstName}&apos;s documents
    </Text>
  );
}
